import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { Session, User, AuthError } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { AuthContextProps, UserRole } from '@/types/auth';
import { checkUserRole } from '@/utils/authHelpers';
import { ensureUserMetrics } from '@/utils/authHelpers';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  isAdmin: boolean;
  userRole: UserRole | null;
  signUp: (email: string, password: string, role: 'investor' | 'entrepreneur') => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithGoogle: (role: 'investor' | 'entrepreneur') => Promise<void>;
  signOut: () => Promise<void>;
  loading: boolean;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      gcTime: 1000 * 60 * 60 * 24, // 24 hours
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
      refetchOnWindowFocus: true,
      refetchOnMount: true,
      refetchOnReconnect: true,
    },
  },
});

// Create a persister with more robust storage
const persister = createSyncStoragePersister({
  storage: window.localStorage,
  key: 'h44s-cache',
  throttleTime: 1000,
  serialize: (data) => JSON.stringify(data),
  deserialize: (data) => JSON.parse(data),
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const navigate = useNavigate();
  const authSubscriptionRef = useRef<{ unsubscribe: () => void } | null>(null);
  const mountedRef = useRef(true);
  const initializationAttempted = useRef(false);

  const clearAuthState = () => {
    setUser(null);
    setSession(null);
    setUserRole(null);
    setIsAdmin(false);
    setLoading(false);
  };

  const handleAuthError = async (error: any) => {
    console.error('Auth error:', error);
    
    if (error?.message?.includes('Invalid Refresh Token') || 
        error?.message?.includes('Refresh Token Not Found')) {
      localStorage.removeItem('h44s-auth');
      await supabase.auth.signOut();
      clearAuthState();
      navigate('/login');
    }
  };

  useEffect(() => {
    let mounted = true;

    const initialize = async () => {
      try {
        setLoading(true);
        
        const { data: { session: initialSession }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          await handleAuthError(sessionError);
          return;
        }

        if (mounted) {
          if (initialSession?.user) {
            setUser(initialSession.user);
            setSession(initialSession);
            const role = await checkUserRole(initialSession.user.id, initialSession.user.email || '');
            setUserRole(role);
            setIsAdmin(role === 'admin');
          } else {
            clearAuthState();
          }
        }

        const { data: { subscription } } = supabase.auth.onAuthStateChange(
          async (event, currentSession) => {
            if (mounted) {
              setLoading(true);
              
              try {
                if (currentSession?.user) {
                  setUser(currentSession.user);
                  setSession(currentSession);
                  const role = await checkUserRole(currentSession.user.id, currentSession.user.email || '');
                  setUserRole(role);
                  setIsAdmin(role === 'admin');
                } else {
                  clearAuthState();
                }
              } catch (error) {
                await handleAuthError(error);
              } finally {
                if (mounted) {
                  setLoading(false);
                }
              }
            }
          }
        );

        authSubscriptionRef.current = subscription;
      } catch (error) {
        await handleAuthError(error);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    initialize();

    return () => {
      mounted = false;
      if (authSubscriptionRef.current) {
        authSubscriptionRef.current.unsubscribe();
      }
    };
  }, [navigate]);

  useEffect(() => {
    const handleFocus = async () => {
      if (!loading && !user) {
        const { data: { session: currentSession } } = await supabase.auth.getSession();
        if (currentSession?.user) {
          setUser(currentSession.user);
          setSession(currentSession);
          const role = await checkUserRole(currentSession.user.id, currentSession.user.email || '');
          setUserRole(role);
          setIsAdmin(role === 'admin');
        }
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [loading, user]);

  useEffect(() => {
    // Check if user has completed wizard
    const checkWizardCompletion = async (userId: string) => {
      try {
        const { data: wizardInfo, error } = await supabase
          .from('wizard_info')
          .select('user_type, completed_at')
          .eq('user_id', userId)
          .single();

        if (error) {
          console.error('Error checking wizard completion:', error);
          return;
        }

        if (wizardInfo?.completed_at) {
          // Auto-redirect based on user type
          if (wizardInfo.user_type === 'investor') {
            navigate('/investor');
          } else if (wizardInfo.user_type === 'entrepreneur') {
            navigate('/entrepreneur');
          }
        }
      } catch (error) {
        console.error('Error:', error);
      }
    };

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (session) {
        setUser(session.user);
        await checkWizardCompletion(session.user.id);
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [navigate]);

  const signInWithGoogle = async (role: 'investor' | 'entrepreneur') => {
    try {
      setLoading(true);
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
          queryParams: {
            role: role
          }
        }
      });

      if (error) throw error;
    } catch (error) {
      await handleAuthError(error);
      throw error;
    }
  };

  const signUp = async (email: string, password: string, role: 'investor' | 'entrepreneur') => {
    try {
      setLoading(true);
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`
        }
      });

      if (error) {
        if (error.message === 'User already registered') {
          throw new Error('This email is already registered. Please sign in instead.');
        }
        throw error;
      }

      const userId = data.user?.id;
      if (!userId) {
        throw new Error('No user ID returned from signup');
      }

      const { error: profileError } = await supabase.from('profiles').insert({
        user_id: userId,
        email: email,
        user_type: role,
        onboarding_completed: false
      });

      if (profileError) {
        console.error('Error creating profile:', profileError);
        toast.error('Failed to create user profile');
        await supabase.auth.signOut();
        throw profileError;
      }

      toast.success('Check your email for the confirmation link');
    } catch (error) {
      await handleAuthError(error);
      const message = error instanceof Error ? error.message : 'Error signing up';
      toast.error(message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      setLoading(true);
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) {
        throw error;
      }

      if (data.session) {
        setSession(data.session);
        setUser(data.session.user);
      }
    } catch (error) {
      await handleAuthError(error);
      toast.error('Invalid email or password');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      setLoading(true);
      
      // First clear local storage
      localStorage.removeItem('h44s-auth');
      
      // Unsubscribe from auth changes
      if (authSubscriptionRef.current) {
        authSubscriptionRef.current.unsubscribe();
      }
      
      // Sign out from Supabase
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      // Clear all auth state
      clearAuthState();
      
      // Force a hard redirect to ensure complete state reset
      window.location.href = '/';
    } catch (error) {
      console.error('Error signing out:', error);
      toast.error('Error signing out');
      // Still clear state even if there's an error
      clearAuthState();
      window.location.href = '/';
    } finally {
      setLoading(false);
    }
  };

  const value = {
    user,
    session,
    isLoading: loading,
    isAdmin,
    userRole,
    signUp,
    signIn,
    signInWithGoogle,
    signOut,
    loading,
  };

  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister,
        dehydrateOptions: {
          shouldDehydrateQuery: (query) => {
            // Only persist non-sensitive queries
            return !query.queryKey.includes('auth');
          },
        },
      }}
    >
      <AuthContext.Provider
        value={{
          user,
          session,
          isLoading: loading,
          isAdmin,
          userRole,
          signUp,
          signIn,
          signInWithGoogle,
          signOut,
          loading,
        }}
      >
        {children}
      </AuthContext.Provider>
    </PersistQueryClientProvider>
  );
};

export default AuthProvider;
